import * as Sentry from '@sentry/browser'
import { Context, Effect, Layer } from 'effect'

export type ErrorReportingUser = { id: string; email?: string }

export type ErrorReportingLevel = 'info' | 'warning' | 'error'

function coerceTags(tags: Record<string, string | number | boolean | undefined>) {
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(tags)) {
    if (v === undefined) continue
    out[k] = String(v)
  }
  return out
}

function coerceExtra(extra: unknown): Record<string, unknown> {
  if (extra && typeof extra == 'object' && !Array.isArray(extra)) {
    return extra as Record<string, unknown>
  }
  return { value: extra }
}

export type ErrorReportingBreadcrumb = {
  category: string
  message: string
  data?: unknown
  level?: ErrorReportingLevel
}

export type ErrorReportingMessage = {
  message: string
  level?: ErrorReportingLevel
  tags?: Record<string, string | number | boolean | undefined>
  extra?: unknown
}

export type ErrorReportingExceptionContext = {
  tags?: Record<string, string | number | boolean | undefined>
  extra?: unknown
}

export type ErrorReportingEvent = {
  message: string
  level?: ErrorReportingLevel
  tags?: Record<string, string | number | boolean | undefined>
  extra?: unknown
}

export type ErrorReportingService = {
  addBreadcrumb: (breadcrumb: ErrorReportingBreadcrumb) => Effect.Effect<void>
  setUser: (user: ErrorReportingUser | null) => Effect.Effect<void>
  setTags: (tags: Record<string, string | number | boolean | undefined>) => Effect.Effect<void>
  captureException: (
    exception: unknown,
    context?: ErrorReportingExceptionContext
  ) => Effect.Effect<void>
  captureMessage: (message: ErrorReportingMessage) => Effect.Effect<void>
  captureEvent: (event: ErrorReportingEvent) => Effect.Effect<void>
}

export class ErrorReporting extends Context.Service<ErrorReporting, ErrorReportingService>()(
  'ErrorReporting'
) {}

export function makeErrorReportingService(): ErrorReportingService {
  return {
    addBreadcrumb: (breadcrumb) => {
      return Effect.sync(() => {
        Sentry.addBreadcrumb({
          type: 'info',
          level: breadcrumb.level ?? 'info',
          category: breadcrumb.category,
          message: breadcrumb.message,
          data: breadcrumb.data === undefined ? undefined : coerceExtra(breadcrumb.data)
        })
      })
    },
    setUser: (user) => {
      return Effect.sync(() => {
        Sentry.setUser(user)
      })
    },
    setTags: (tags) => {
      return Effect.sync(() => {
        Sentry.setTags(coerceTags(tags))
      })
    },
    captureException: (exception, context) => {
      return Effect.sync(() => {
        Sentry.withScope((scope) => {
          if (context?.tags) {
            scope.setTags(coerceTags(context.tags))
          }
          if (context?.extra !== undefined) {
            scope.setExtra('extra', context.extra)
          }
          Sentry.captureException(exception)
        })
      })
    },
    captureMessage: (message) => {
      return Effect.sync(() => {
        Sentry.withScope((scope) => {
          if (message.level) {
            scope.setLevel(message.level)
          }
          if (message.tags) {
            scope.setTags(coerceTags(message.tags))
          }
          if (message.extra !== undefined) {
            scope.setExtra('extra', message.extra)
          }

          Sentry.captureMessage(message.message)
        })
      })
    },
    captureEvent: (event) => {
      return Effect.sync(() => {
        Sentry.captureEvent({
          message: event.message,
          level: event.level,
          tags: event.tags ? coerceTags(event.tags) : undefined,
          extra: event.extra === undefined ? undefined : coerceExtra(event.extra)
        })
      })
    }
  }
}

export function errorReportingLayer(service: ErrorReportingService = makeErrorReportingService()) {
  return Layer.succeed(ErrorReporting, service)
}

export function errorReportingNoopLayer() {
  return Layer.succeed(ErrorReporting, {
    addBreadcrumb: () => Effect.void,
    setUser: () => Effect.void,
    setTags: () => Effect.void,
    captureException: () => Effect.void,
    captureMessage: () => Effect.void,
    captureEvent: () => Effect.void
  })
}
